import { Formik } from 'formik';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { date, number, object } from 'yup';
import { PrimaryButton } from '../../../../components/Buttons/PrimaryButton';
import { SecondaryButton } from '../../../../components/Buttons/SecondaryButton';
import { Form, FormInputControl, FormMobisPicker } from '../../../../components/forms';
import { CenteredLoadingIndicator } from '../../../../components/LoadingIndicator';
import ServerValidationBox from '../../../../components/ServerValidationBox';
import { ButtonSizeEnum } from '../../../../constants';
import { PHRTrackerBloodPressureDTO } from '../../../../generated';
import { createForPhrSection, updateForPhrSection } from '../../../../store/root-creator';
import { AppState } from '../../../../store/root-reducers';
import { ReduxPHRCategoryEnum } from '../../../../store/types';
import { ButtonRow } from '../../components/StyledPhrItems';

interface IProps {
  selectedData: PHRTrackerBloodPressureDTO | null;
  patientID: string;
  toggleView: () => void;
}

interface IForm {
  trackerDate: moment.Moment;
  systolic?: number;
  diastolic?: number;
}

const BloodPressureForm = ({ selectedData, patientID, toggleView }: IProps) => {
  const dispatch = useDispatch();
  const { error, isPhrLoading } = useSelector((state: AppState) => state.phrPatientState);
  const [isSubmitting, setSubmitting] = useState<boolean>(false);

  const onSubmit = async (values: IForm) => {
    setSubmitting(true);
    if (selectedData) {
      selectedData.trackerDate = moment(values.trackerDate).toDate();
      selectedData.systolic = values.systolic;
      selectedData.diastolic = values.diastolic;
      await dispatch(
        updateForPhrSection(ReduxPHRCategoryEnum.PHRBloodPressures, selectedData, patientID, true),
      );
    } else {
      await dispatch(
        createForPhrSection(ReduxPHRCategoryEnum.PHRBloodPressures, values, patientID, true),
      );
    }
  };

  useEffect(() => {
    if (isSubmitting && !isPhrLoading) {
      if (error) {
        setSubmitting(false);
      } else {
        toggleView();
      }
    }
  }, [error, isPhrLoading, isSubmitting, toggleView]);

  const initialValues: IForm = {
    trackerDate: selectedData ? moment(selectedData.trackerDate!) : moment(),
    systolic: selectedData ? selectedData.systolic : undefined,
    diastolic: selectedData ? selectedData.diastolic : undefined,
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={object({
        trackerDate: date().required('Date is required').typeError('Invalid date'),
        systolic: number()
          .required('A systolic value is required')
          .positive('The systolic value must be a postive number')
          .typeError('Systolic must be a postive number')
          .moreThan(0, 'Value must be greater than 0')
          .lessThan(300, 'Value must be less than 300'),
        diastolic: number()
          .required('A diastolic value is required')
          .positive('The diastolic value must be a postive number')
          .typeError('Diastolic must be a postive number')
          .moreThan(0, 'Value must be greater than 0')
          .lessThan(300, 'Value must be less than 300'),
      })}
      onSubmit={onSubmit}
    >
      {({ status }) => (
        <Form error={error}>
          {isSubmitting ? (
            <CenteredLoadingIndicator />
          ) : (
            <>
              <ServerValidationBox message={status} />
              <div className="row">
                <div className="col-12 col-sm-6">
                  <FormMobisPicker
                    name="trackerDate"
                    label="Tracked at"
                    controls={['calendar', 'time']}
                    max={moment().toDate()}
                    min={moment().add(-150, 'year').toDate()}
                  />
                  {/* <FormDateTimePicker
                    name="trackerDate"
                    label="Tracked at"
                    showTimeSelect
                    maxDate={moment().toDate()}
                    minDate={moment().add(-150, 'year').toDate()}
                  /> */}
                </div>
              </div>
              <div className="row">
                <div className="col-12 col-sm-6">
                  <FormInputControl name="systolic" label="Systolic" type="text" />
                </div>
                <div className="col-12 col-sm-6">
                  <FormInputControl name="diastolic" label="Diastolic" type="text" />
                </div>
              </div>
              <ButtonRow>
                <SecondaryButton size={ButtonSizeEnum.medium} onClick={toggleView}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton size={ButtonSizeEnum.medium} type="submit">
                  Save
                </PrimaryButton>
              </ButtonRow>
            </>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default BloodPressureForm;
